import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../auth/store';
import { useTenant, useToggleTenantStatus, useGenerateTenantApiKey } from './hooks/useTenants';
import { useMerchantUsers } from '../merchants/hooks/useMerchants';
import { useSolutions } from '../metadata/hooks/useMetadata';
import { usePermissions, Permission } from '../../shared/hooks/usePermissions';
import { useTenantStore } from '../../shared/stores/useTenantStore';
import Button from '../../shared/components/Button';
import { DomainFieldBuilder } from '../field-config/components/DomainFieldBuilder';
import { ApiKeyModal } from './components/ApiKeyModal';
import type { Tenant } from './types';
import type { Merchant } from '../merchants/types';

function formatDate(d: string | null) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatDateTime(d: string | null) {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

// Tenant-scoped tool pages (Booking Services, Homepage Slider…) read the
// "current merchant" from useTenantStore, which is typed to the full commerce
// Merchant shape. A tenant IS a row of the same table, minus the commerce-only
// columns — map it explicitly with inert commerce defaults rather than widening
// the shared store type (the pages only read id/name/slug/solutionId).
function tenantAsMerchant(t: Tenant): Merchant {
  return {
    id: t.id,
    name: t.name,
    slug: t.slug,
    companyAddress: t.companyAddress,
    commissionCharge: 0,
    contractType: 'COMMISSION',
    invoicingCadence: 'WEEKLY',
    invoicingSchedule: null,
    customBuildPrice: null,
    platformFee: null,
    platformFeeFrequency: null,
    installmentPlan: null,
    active: t.active,
    contractStartDate: t.createdAt,
    contractEndDate: null,
    nextContractReviewDate: null,
    partnerSuccessBlueprintUrl: null,
    clarityProjectId: t.clarityProjectId,
    bpmOfficerId: null,
    liaisonId: null,
    bpmOfficer: null,
    liaison: null,
    solutionId: t.solutionId,
    createdAt: t.createdAt,
    updatedAt: t.updatedAt,
    industries: [],
    businessCategories: [],
    invitePending: t.invitePending,
    lastInvitedAt: t.lastInvitedAt,
  };
}

export default function TenantDetail({ id }: { id: string }) {
  const navigate = useNavigate();

  const { data: tenant, isLoading, error } = useTenant(id);
  // Watchman's GET /merchants/:id/users has no tenant-type filter (single tenant
  // table), so the merchants users hook works verbatim for a brand-website tenant id.
  const { data: users = [] } = useMerchantUsers(id);
  const { data: solutions = [] } = useSolutions();

  const toggleStatus = useToggleTenantStatus();
  const generateKey = useGenerateTenantApiKey();
  const { hasPermission } = usePermissions();
  const setActiveMerchant = useTenantStore((s) => s.setActiveMerchant);
  const setSelectedTenantId = useAuthStore((s) => s.setSelectedTenantId);

  // Tenant-scoped tools live as their own pages that read the "current merchant"
  // from a store. Pin this tenant into BOTH stores (some pages read useTenantStore's
  // activeMerchant, others read useAuthStore's selectedTenantId) before navigating —
  // same two-store pattern as MerchantDetail's manageForMerchant.
  const manageForTenant = (path: string) => {
    setActiveMerchant(tenant ? tenantAsMerchant(tenant) : null);
    setSelectedTenantId(tenant?.id ?? null);
    navigate(path);
  };

  const [newKey, setNewKey] = useState<string | null>(null);

  const canDeactivate = hasPermission(Permission.TENANT_DEACTIVATE);
  const canMintApiKey = hasPermission(Permission.API_KEY_CREATE);
  // Intake field configuration is TCS-staff-only (merchants/tenants never see
  // intake config). MERCHANT_VIEW is the established "COLLECTIVE staff managing
  // other tenants" gate (see Sidebar's canManageViaMerchantDetail).
  const canConfigureIntake = hasPermission(Permission.MERCHANT_VIEW);

  const handleGenerate = async () => {
    try {
      const result = await generateKey.mutateAsync(id);
      setNewKey(result.apiKey);
    } catch {
      // Error toast already raised by the mutation's onError.
    }
  };

  if (isLoading) return <div className="py-12 text-center text-foreground">Loading…</div>;
  if (error || !tenant) return <div className="py-12 text-center text-red-500">Tenant not found.</div>;

  const solutionName = solutions.find((s) => s.slug === tenant.solutionId)?.name ?? tenant.solutionId;

  // Per-type tools: BRAND_WEBSITE tenants get website surfaces only (no commerce
  // catalogue/orders/payouts — those stay merchant tools).
  const tools = [
    {
      label: 'Booking Services',
      description: 'Bookable services visitors can schedule through this tenant\'s website.',
      path: '/booking-services',
      show: hasPermission(Permission.BOOKING_SERVICE_VIEW),
    },
    {
      label: 'Homepage Slider',
      description: 'Slides shown on this tenant\'s website homepage.',
      path: '/homepage-slider',
      show: hasPermission(Permission.SLIDER_VIEW),
    },
  ].filter((t) => t.show);

  return (
    <div className="space-y-6 py-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/tenants')} className="text-foreground hover:text-foreground text-sm underline">
            ← Tenants
          </button>
          <span className="text-muted-foreground">/</span>
          <h1 className="text-2xl font-bold text-foreground">{tenant.name}</h1>
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${tenant.active ? 'bg-green-100 text-green-800' : 'bg-muted text-muted-foreground'}`}>
            {tenant.active ? 'Active' : 'Inactive'}
          </span>
          {tenant.invitePending && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
              Invite pending
            </span>
          )}
        </div>
        {canDeactivate && (
          <button
            onClick={() => toggleStatus.mutate({ id: tenant.id, active: tenant.active })}
            disabled={toggleStatus.isPending}
            className={`px-4 py-2 rounded-md text-sm font-medium border ${tenant.active ? 'border-red-300 text-red-600 hover:bg-red-50' : 'border-green-300 text-green-700 hover:bg-green-50'}`}
          >
            {tenant.active ? 'Deactivate' : 'Activate'}
          </button>
        )}
      </div>

      {/* Identity */}
      <div className="bg-card rounded-lg border border-border p-6">
        <h2 className="text-base font-semibold text-foreground mb-4">Details</h2>
        <dl className="grid grid-cols-2 gap-x-8 gap-y-3 text-sm">
          <div>
            <dt className="text-foreground">Slug</dt>
            <dd className="font-medium text-foreground font-mono">{tenant.slug}</dd>
          </div>
          <div>
            <dt className="text-foreground">Type</dt>
            <dd className="font-medium text-foreground">{tenant.type === 'BRAND_WEBSITE' ? 'Brand Website' : 'E-commerce'}</dd>
          </div>
          <div>
            <dt className="text-foreground">Solution</dt>
            <dd className="font-medium text-foreground">{solutionName}</dd>
          </div>
          <div>
            <dt className="text-foreground">Clarity Project</dt>
            <dd className="font-medium text-foreground">{tenant.clarityProjectId ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-foreground">Created</dt>
            <dd className="font-medium text-foreground">{formatDate(tenant.createdAt)}</dd>
          </div>
          <div>
            <dt className="text-foreground">Last Updated</dt>
            <dd className="font-medium text-foreground">{formatDate(tenant.updatedAt)}</dd>
          </div>
          {tenant.invitePending && (
            <div>
              <dt className="text-foreground">Last Invited</dt>
              <dd className="font-medium text-foreground">{formatDateTime(tenant.lastInvitedAt)}</dd>
            </div>
          )}
          <div className="col-span-2">
            <dt className="text-foreground">Company Address</dt>
            <dd className="font-medium text-foreground whitespace-pre-wrap">{tenant.companyAddress || '—'}</dd>
          </div>
        </dl>
      </div>

      {/* API Key — no read endpoint exists on the tenant contract, only the derived
          hasApiKey flag and the mint/rotate action (raw key shown once). */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold text-foreground">API Key</h2>
            {tenant.invitePending ? (
              <p className="text-xs text-amber-600 mt-0.5">Tenant must complete onboarding before an API key can be issued.</p>
            ) : (
              <p className="text-sm text-foreground mt-1">
                {tenant.hasApiKey
                  ? 'This tenant has an active API key. Rotating revokes the current key and returns a new one.'
                  : 'No active API key. Generate one to allow this tenant\'s website to access the platform.'}
              </p>
            )}
          </div>
          {canMintApiKey && (
            <Button
              onClick={handleGenerate}
              loading={generateKey.isPending}
              disabled={tenant.invitePending}
              title={tenant.invitePending ? 'Tenant has not completed onboarding' : undefined}
            >
              {tenant.hasApiKey ? 'Rotate Key' : 'Generate Key'}
            </Button>
          )}
        </div>
      </div>

      {/* Users */}
      <div className="bg-card rounded-lg border border-border p-6">
        <h2 className="text-base font-semibold text-foreground mb-4">Users ({users.length})</h2>
        {users.length === 0 ? (
          <p className="text-sm text-foreground">No users assigned to this tenant yet.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-2 font-semibold text-foreground">Name</th>
                <th className="text-left py-2 font-semibold text-foreground">Email</th>
                <th className="text-left py-2 font-semibold text-foreground">Phone</th>
                <th className="text-left py-2 font-semibold text-foreground">Role</th>
                <th className="text-left py-2 font-semibold text-foreground">Verified</th>
                <th className="text-left py-2 font-semibold text-foreground">Profile</th>
                <th className="text-left py-2 font-semibold text-foreground">Joined</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id} className="border-b border-border">
                  <td className="py-2 font-medium text-foreground">{user.fullName}</td>
                  <td className="py-2 text-foreground">{user.email}</td>
                  <td className="py-2 text-foreground">{user.phoneNumber ?? '—'}</td>
                  <td className="py-2 text-foreground">{user.role.name.replace(/_/g, ' ')}</td>
                  <td className="py-2">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${user.isEmailVerified ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                      {user.isEmailVerified ? 'Yes' : 'Pending'}
                    </span>
                  </td>
                  <td className="py-2">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${user.isProfileCompleted ? 'bg-green-100 text-green-800' : 'bg-muted text-muted-foreground'}`}>
                      {user.isProfileCompleted ? 'Complete' : 'Incomplete'}
                    </span>
                  </td>
                  <td className="py-2 text-foreground">{formatDate(user.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Tenant Tools — per-tenant surfaces managed from here by COLLECTIVE staff.
          Each row pins THIS tenant into both scope stores then opens the page. */}
      {tools.length > 0 && (
        <div className="bg-card rounded-lg border border-border p-6">
          <h2 className="text-base font-semibold text-foreground mb-1">Tenant Tools</h2>
          <p className="text-sm text-muted-foreground mb-4">Manage this tenant's website services and content.</p>
          <div className="divide-y divide-border">
            {tools.map((tool) => (
              <div key={tool.path} className="flex items-center justify-between py-3 first:pt-0 last:pb-0">
                <div className="pr-4">
                  <div className="text-sm font-medium text-foreground">{tool.label}</div>
                  <p className="text-sm text-muted-foreground mt-0.5">{tool.description}</p>
                </div>
                <Button variant="outline" size="sm" onClick={() => manageForTenant(tool.path)}>
                  Manage
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Intake Field Builder — TCS staff configure the intake forms (booking,
          custom order, package) served on this tenant's website. */}
      {canConfigureIntake && (
        <div className="bg-card rounded-lg border border-border p-6">
          <div className="mb-4">
            <h2 className="text-base font-semibold text-foreground">Intake Field Configuration</h2>
            <p className="text-sm text-foreground mt-0.5">
              Customise the fields visitors fill in for Booking, Custom Order, and Package services.
            </p>
          </div>
          {tenant.solutionId ? (
            <DomainFieldBuilder merchantId={tenant.id} solutionId={tenant.solutionId} />
          ) : (
            <p className="text-sm text-foreground">No solution ID assigned to this tenant — field configuration is unavailable.</p>
          )}
        </div>
      )}

      {newKey && <ApiKeyModal apiKey={newKey} onClose={() => setNewKey(null)} />}
    </div>
  );
}
